import { alpha, createTheme, type PaletteColor, type PaletteColorOptions } from '@mui/material/styles'
import { foundations, GREY } from './theme/foundations'
// Side-effect import: registers the `MuiDataGrid` component key on MUI's
// `Components` type so DataGrid `defaultProps` (density) type-check in the theme.
import '@mui/x-data-grid-premium/themeAugmentation'
import type { GridLocaleText } from '@mui/x-data-grid-premium'

// MUI X's default label interpolates raw numbers, so a large total renders as "1–25 of 12345".
export const paginationDisplayedRows: GridLocaleText['paginationDisplayedRows'] = ( { from, to, count, estimated } ) => {
	const format = ( n: number ) => n.toLocaleString()
	if ( count !== -1 ) {
		return `${format( from )}–${format( to )} of ${format( count )}`
	}
	const total = estimated && estimated > to ? `around ${format( estimated )}` : `more than ${format( to )}`
	return `${format( from )}–${format( to )} of ${total}`
}

// Custom palette extensions. Per our MUI conventions, every color the app uses
// should be a named palette entry rather than a hardcoded hex/rgba at the call
// site — these back the dc-admin header and the transfer-order form chrome.
type DcAdminHeaderPalette = {
	background: string,
	contrastTextMuted: string,
	contrastTextSubtle: string,
	contrastBorder: string,
}

type FormPanelPalette = {
	footerBackground: string,
	errorBackground: string,
	errorText: string,
	warningBackground: string,
	warningBorder: string,
	shadow: string,
}

// Inventory-specific semantic status colors for stock requests. Graduated from
// the design theme reference (docs/inventory-theme-reference.html) into code so
// the live /theme-reference route renders them from the theme (no drift).
type StatusColor = {
	main: string,
	light: string,
	dark: string,
}

type StockRequestStatusPalette = {
	open: StatusColor,
	assigned: StatusColor,
	overdue: StatusColor,
	completed: StatusColor,
}

// Surfaces for the mini-DC pick flow (/dc-admin/pick-orders). Those screens are
// run on a tablet by someone walking the floor, so they deliberately do not read
// like the dense /dc-admin console — they get their own named surfaces rather
// than borrowing the data grid's greys at the call site.
type PickFlowPalette = {
	surface: string,
	stepActiveBackground: string,
	stepCompleteBackground: string,
	cardBorder: string,
	// A short pick (fewer found than called for) is a normal outcome, not an
	// error — it gets a caution surface, never the error red.
	shortPickBackground: string,
	shortPickBorder: string,
	keypadBackground: string,
}

// Touch sizing for the tablet pick flow. These are dimensions, not colors, so
// they live on their own top-level theme key rather than in the palette; the
// pick screens read them as `theme.touch.*` instead of scattering pixel
// literals through `sx`. Set here up front so touch sizing is a property of the
// theme the screens are built against, not something retrofitted onto them.
type TouchTokens = {
	/** WCAG 2.5.5 (AAA) minimum target size — the floor for anything tappable. */
	minTarget: number,
	/** Default target for buttons and list rows in the pick flow. */
	target: number,
	/** Primary controls: quantity steppers, step navigation, carton rows. */
	control: number,
	/** One key of the quantity keypad. */
	keypadKey: number,
	/** Minimum gap between adjacent targets, so a fingertip cannot straddle two. */
	gap: number,
}

/* eslint-disable @typescript-eslint/consistent-type-definitions -- module augmentation requires `interface` for declaration merging */
declare module '@mui/material/styles' {
	interface Palette {
		dcAdminHeader: DcAdminHeaderPalette,
		formPanel: FormPanelPalette,
		stockRequestStatus: StockRequestStatusPalette,
		pickFlow: PickFlowPalette,
		// General-purpose neutral/grey ramp for borders, dividers, muted surfaces.
		neutral: PaletteColor,
	}
	interface PaletteOptions {
		dcAdminHeader?: DcAdminHeaderPalette,
		formPanel?: FormPanelPalette,
		stockRequestStatus?: StockRequestStatusPalette,
		pickFlow?: PickFlowPalette,
		neutral?: PaletteColorOptions,
	}
	interface Theme {
		touch: TouchTokens,
	}
	interface ThemeOptions {
		touch?: TouchTokens,
	}
}
/* eslint-enable @typescript-eslint/consistent-type-definitions */

const WHITE = '#ffffff'
const BLACK = '#000000'
const WARNING = '#FEC514' // EUI warning yellow, kept for visual parity during migration

// Touch sizing shared by every pick-flow screen. 44px is the WCAG 2.5.5 floor;
// 48px is the working default so a target still clears the floor after a 1-2px
// border, and 56/64px are for the controls a picker hits repeatedly one-handed.
const TOUCH: TouchTokens = {
	minTarget: 44,
	target: 48,
	control: 56,
	keypadKey: 64,
	gap: 12,
}

/**
 * App-wide MUI theme for the DS2 → MUI migration.
 *
 * Layered so the shareable core can be extracted later:
 *   - `foundations` (src/config/theme/foundations.ts) — palette + typography,
 *     aligned to the 1854 base theme. The future shared-package seed.
 *   - this file — app-specific additions on top: custom palette slots (dc-admin
 *     header, transfer-order form chrome), the neutral ramp, and the app-wide
 *     compact-density `defaultProps` for a dense data app.
 *
 * The shared layer stays foundations-only: NO component `styleOverrides` (see the
 * migration plan's theme stop-line) and NO domain palettes from the source theme
 * (deliveryType / blueGrey / panelBackground).
 *
 * NOTE: the 1854 palette/type VALUES are provisional pending confirmation of the
 * canonical 1854 token source; only the values should change, not the structure.
 */
const muiTheme = createTheme( {
	...foundations,
	palette: {
		...foundations.palette,
		// General-purpose neutral ramp (borders, dividers, muted surfaces/text).
		neutral: {
			main: GREY[500],
			light: GREY[100],
			dark: GREY[800],
			contrastText: WHITE,
		},
		// DC Admin page header: dark navy bar with translucent white controls.
		dcAdminHeader: {
			background: '#1e3a5f',
			contrastTextMuted: alpha( WHITE, 0.7 ),
			contrastTextSubtle: alpha( WHITE, 0.6 ),
			contrastBorder: alpha( WHITE, 0.3 ),
		},
		// Shared sticky footer / error-panel chrome for the transfer-order forms.
		formPanel: {
			footerBackground: '#424242',
			errorBackground: '#FFF5F5',
			errorText: '#BD271E', // EUI danger red, kept for visual parity during migration
			warningBackground: alpha( WARNING, 0.12 ),
			warningBorder: WARNING,
			shadow: `0 -2px 4px ${alpha( BLACK, 0.1 )}`, // upward elevation for the sticky footer / error panel
		},
		// Stock-request status colors (light mode). Base-token alignment per design:
		// open→blueGrey[500], assigned→deliveryType.finalReturn, overdue→warning, completed→deliveryType.move.
		stockRequestStatus: {
			open: { main: '#607d8b', light: '#b0bec5', dark: '#546e7a' },
			assigned: { main: '#4a98c2', light: '#78c4e6', dark: '#356d8a' },
			overdue: { main: '#ed6c02', light: '#ffa726', dark: '#c43c00' },
			completed: { main: '#68ceae', light: '#9de4d0', dark: '#4a9e87' },
		},
		// Mini-DC pick-flow surfaces. Derived from the foundations palette rather
		// than new hexes, so the pick screens stay on the app's color story while
		// reading as their own (roomier, tablet-first) surface.
		pickFlow: {
			surface: WHITE,
			stepActiveBackground: alpha( foundations.palette.primary.main, 0.06 ),
			stepCompleteBackground: alpha( foundations.palette.success.main, 0.08 ),
			cardBorder: GREY[300],
			shortPickBackground: alpha( WARNING, 0.14 ),
			shortPickBorder: WARNING,
			keypadBackground: GREY[100],
		},
	},
	// Touch sizing tokens — see TOUCH above. Available app-wide (typed on Theme)
	// but only the pick flow is built against them today.
	touch: TOUCH,
	typography: {
		...foundations.typography,
		// `button` isn't part of the base scale — buttons read as labels, not shouted chrome.
		button: { textTransform: 'none', fontWeight: 500 },
		// EUI's global reset (mounted alongside MUI for the whole coexistence
		// period — sc-563618) sets `html { font-size: 14px }`, not the browser's
		// 16px default. `htmlFontSize` tells MUI's own `pxToRem()` the real root
		// size, so every variant MUI computes itself (body1/body2/caption/button/
		// overline — anything `foundations.ts` doesn't override) renders at its
		// intended px size against that real root instead of 87.5% of it. This is
		// app-specific (a plain-MUI host without EUI has a real 16px root and
		// wouldn't want this), so it lives here, not in `foundations.ts`.
		//
		// NOT tied to this migration's own Phase 4: the teardown plan keeps
		// `EuiProvider` mounted (optionally narrowed to wrap only `<AppHeader/>`)
		// until `@equipmentshare/universal-header` ships DS2-free — and its
		// global reset injects page-wide regardless of where it mounts, so
		// narrowing the provider does NOT remove this collision. Delete this
		// line only once `EuiProvider` is gone from the app entirely (see the
		// Risk Register's "Universal header gates final DS2 removal").
		htmlFontSize: 14,
	},
	// Coexistence layering (sc-548647). EUI's level scale sits far above MUI's:
	// EUI toast 9000 / modal 8000 / mask 6000 vs MUI's default ceiling of 1500
	// (tooltip). Only MUI's *portal overlays* need lifting — a popper opened
	// inside an EUI modal otherwise renders behind it. They land between EUI's
	// modal and toast levels, keeping MUI's internal order and leaving EUI
	// toasts on top.
	//
	// In-page chrome (appBar, drawer, fab, speedDial, mobileStepper) keeps MUI's
	// defaults on purpose: the app-shell sidebar is a permanent MUI `Drawer`, so
	// lifting `drawer` floats it over EUI's modal mask. Chrome belongs *under*
	// EUI overlays. Delete this block at teardown.
	zIndex: {
		modal: 8400,
		snackbar: 8500,
		tooltip: 8600,
	},
	// Density policy — "compact inputs, medium buttons" (sc-538984). Compact
	// density is a parity mechanism for information-dense data views: the argument
	// is strongest for fields/selects/table rows that stack vertically and drive
	// rows-per-screen, and weakest for action buttons. So inputs + data controls
	// default to compact, while the button family keeps MUI's default (medium).
	// (Layer 2 — app policy; still `defaultProps`, within the theme stop-line.)
	components: {
		// Compact — inputs, selection, and data controls.
		MuiTextField: { defaultProps: { size: 'small' } },
		MuiFormControl: { defaultProps: { size: 'small' } },
		MuiCheckbox: { defaultProps: { size: 'small' } },
		MuiRadio: { defaultProps: { size: 'small' } },
		MuiSwitch: { defaultProps: { size: 'small' } },
		MuiAutocomplete: { defaultProps: { size: 'small' } },
		// Field labels deliberately carry NO font-size override, reversing the
		// compact-label default from sc-548648. A floating label rests in the
		// input-text position and is replaced by that text, so anything smaller
		// than `body1` reads as undersized next to the value the user types —
		// which is what it did. Inheriting keeps the two locked together; a
		// literal here would drift the moment the input scale changes.
		//
		// (It was `0.8125rem`: 13/16, a 13px label written against a standard
		// root. EUI's reset makes the real root 14px, so it rendered 11.375px,
		// and 8.5px once shrunk by MUI's 0.75 scale. `htmlFontSize` could not
		// correct it — that only reaches what MUI computes via `pxToRem`, never
		// a literal. sc-563618 swept `foundations.ts`, not component overrides.)
		//
		// The weight override stays: density is carried by weight, not size.
		MuiInputLabel: { styleOverrides: { root: { fontWeight: 500 } } },
		MuiChip: { defaultProps: { size: 'small' } }, // status pills stay compact (plan: EuiBadge → Chip)
		MuiTable: { defaultProps: { size: 'small' } },
		MuiDataGrid: {
			defaultProps: { density: 'compact', localeText: { paginationDisplayedRows } },
			styleOverrides: {
				// A grid cell is a block whose line-height fills the row, which centres
				// text but drops an inline chip onto that line's baseline. Flex the cell
				// so the pill centres and sits on the same left gutter as text cells.
				cell: {
					'&:has(> .MuiChip-root)': { display: 'flex', alignItems: 'center', lineHeight: 'normal' },
				},
			},
		},
		// MUI's built-in h6 assumes its own 20px h6; ours is 14px, so point dialog titles at h4 (20px).
		MuiDialogTitle: { defaultProps: { variant: 'h4' } },
		// EuiModal parity: a 400px floor so width-less dialogs don't shrink to their content. EUI dropped it on small screens too.
		MuiDialog: {
			styleOverrides: {
				paper: ( { theme } ) => ( { [ theme.breakpoints.up( 'sm' ) ]: { minWidth: 400 } } ),
			},
		},
		// Buttons — left at MUI default (medium); intentionally not compacted:
		// MuiButton, MuiButtonGroup, MuiIconButton, MuiFab, MuiToggleButton,
		// MuiToggleButtonGroup.
	},
} )

/**
 * Scoped theme for the mini-DC pick flow (`/dc-admin/pick-orders`).
 *
 * The rest of `/dc-admin` is a dense desktop admin console: compact inputs,
 * `SearchFilterExperience` grids, small controls. The pick flow is the opposite
 * — one tablet, one person, gloves on, one step at a time — so it overrides the
 * app-wide compact-density policy instead of fighting it per component with
 * `size="large"` and `minHeight` sprinkled through `sx`.
 *
 * Applied by wrapping only the pick-order screens in
 * `<ThemeProvider theme={pickFlowTheme}>`. Existing `/dc-admin` screens are untouched.
 *
 * Layer 3 (per-app `styleOverrides`) — deliberately not in `foundations`.
 */
export const pickFlowTheme = createTheme( muiTheme, {
	// Larger type scale: read at arm's length, on a tablet, in a warehouse.
	typography: {
		body1: { fontSize: '1.0625rem', lineHeight: 1.5 },
		body2: { fontSize: '0.9375rem', lineHeight: 1.5 },
		h4: { fontSize: '1.75rem', fontWeight: 700 },
		h5: { fontSize: '1.375rem', fontWeight: 700 },
		h6: { fontSize: '1.125rem', fontWeight: 700 },
		subtitle2: { fontSize: '1rem', fontWeight: 600 },
		button: { fontSize: '1rem', fontWeight: 600, textTransform: 'none' },
	},
	components: {
		// Undo the app-wide compact density (see the density policy above): every
		// control the picker touches goes back to MUI's roomier default.
		MuiTextField: { defaultProps: { size: 'medium' } },
		MuiFormControl: { defaultProps: { size: 'medium' } },
		MuiCheckbox: { defaultProps: { size: 'medium' } },
		MuiRadio: { defaultProps: { size: 'medium' } },
		MuiSwitch: { defaultProps: { size: 'medium' } },
		MuiAutocomplete: { defaultProps: { size: 'medium' } },
		MuiChip: { defaultProps: { size: 'medium' } },
		MuiTable: { defaultProps: { size: 'medium' } },
		// Hit targets. `minHeight` (not `height`) so a control can still grow with
		// its content; every value is >= TOUCH.minTarget.
		MuiButton: {
			defaultProps: { size: 'large', disableElevation: true },
			styleOverrides: {
				root: {
					minHeight: TOUCH.target,
					paddingLeft: 20,
					paddingRight: 20,
					borderRadius: 8,
				},
			},
		},
		MuiIconButton: {
			defaultProps: { size: 'large' },
			styleOverrides: {
				root: { minWidth: TOUCH.target, minHeight: TOUCH.target },
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: { minWidth: TOUCH.target, minHeight: TOUCH.target },
			},
		},
		MuiListItemButton: {
			styleOverrides: { root: { minHeight: TOUCH.control } },
		},
		MuiInputBase: {
			styleOverrides: { root: { minHeight: TOUCH.target, fontSize: '1.0625rem' } },
		},
		MuiTableCell: {
			styleOverrides: { root: { paddingTop: 14, paddingBottom: 14, fontSize: '0.9375rem' } },
		},
		MuiStepLabel: {
			styleOverrides: { label: { fontSize: '1rem', fontWeight: 600 } },
		},
		MuiStepIcon: {
			styleOverrides: { root: { fontSize: '1.75rem' } },
		},
	},
} )

export default muiTheme
